import { memo, useContext, useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Link, matchRoutes, useLocation } from 'react-router-dom';
import type { DefaultTheme } from 'styled-components';
import { ThemeProvider, ThemeContext } from 'styled-components';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import { Media } from '../../../../theme/Media.tsx';
import { Tooltip } from '../../../Feedback/Tooltip/index.tsx';
import { palette } from '../../../Palette/index.ts';
import { usePuzzleNavigationContext } from '../context.tsx';
import type { RibbonVariant } from '../types.ts';
import { CornerBadge } from './CornerBadge.tsx';
import type { IconType } from './Item/HeaderWrapper.tsx';
import { HeaderWrapper } from './Item/HeaderWrapper.tsx';
import { ItemElement, ItemHeader } from './Item/styled.ts';

export interface IItemLink {
  icon: IconType;
  title: string;
  exact?: boolean;
  path: string | string[];
  to: string;
  dataTestId?: string;
  label?: string;
  theme?: DefaultTheme;
  ribbon?: RibbonVariant;
  showIndicator?: boolean;
}

const ItemLink = ({ icon, title, exact, path: pathProp, to, dataTestId, label, theme, ribbon, showIndicator }: IItemLink) => {
  const { setActiveKey, isCollapsedNav, collapseMainNav } = usePuzzleNavigationContext();
  const isMobile = useMediaQuery({ query: Media.xsMax });

  const currentTheme = useContext(ThemeContext);
  const effectiveTheme = theme ?? currentTheme ?? palette.primary;

  const handleCloseMenu = useCallbackRef(() => {
    setActiveKey(null);
    if (to && isMobile) {
      collapseMainNav();
    }
  });

  const location = useLocation();
  const paths = useMemo(
    () =>
      (Array.isArray(pathProp) ? pathProp : [pathProp]).map((path) => ({
        path: exact ? path : `${path}/*`,
      })),
    [exact, pathProp],
  );

  const match = useMemo(() => matchRoutes(paths, location), [location, paths]);

  return (
    <ThemeProvider theme={effectiveTheme}>
      <Tooltip tooltip={title} placement='right' ContainerComponent={ItemElement} disabled={!isCollapsedNav} zIndex={11}>
        <ItemHeader as={Link} $collapsed={isCollapsedNav} onClick={handleCloseMenu} to={to} data-testid={dataTestId}>
          {ribbon && <CornerBadge variant={ribbon} />}
          <HeaderWrapper title={title} match={!!match} icon={icon} label={label} showIndicator={showIndicator} />
        </ItemHeader>
      </Tooltip>
    </ThemeProvider>
  );
};

export default memo(ItemLink);
